import 'twin.macro';
import 'styled-components/macro';
import { Fragment, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as CloseIcon } from 'component-lib/atoms/icon-v2/CloseIcon.svg';
import { CarouselPagination } from 'play/molecules/carousel/CarouselPagination';
import { ChallengeNotes, Note } from 'play/organisms/notes-screen/ChallengeNotes';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { ChallengeNoteType, LearnEnvironmentInviteQuery, LearnEnvironmentQuery } from 'types/graphql';

import { useNotificationContext } from '../../../core/context/Notifications';
import { getAccessSetting } from '../../../support/accessSetting';
import { getShareURL } from '../../../support/invite';
import { ShareButton } from '../../molecules/share-button/ShareButton';
import DarkOverlay from '../../templates/dark-overlay/DarkOverlay';

const Close = styled.a`
  cursor: pointer;
  z-index: 2000;
  img {
    width: 50px;
    height: 50px;
  }
`;

interface NotesOverlayProps {
  teamSlug: string;
  notes: NonNullable<LearnEnvironmentQuery['challenge']>['notes'];
  toggleNotes(): void;
  invite?: LearnEnvironmentInviteQuery['trackInvite'];
}

export const NotesOverlay = ({ teamSlug, notes, invite, toggleNotes }: NotesOverlayProps) => {
  const { dispatch } = useNotificationContext();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const [activeIndex, setActiveIndex] = useState(0);
  const showShareButton = invite?.canClaim && invite?.enableLearnerSharing;

  const filteredNotes = notes
    .filter(n => (n.type === ChallengeNoteType.Text && n.contents) || n.url)
    .map<Note>(n =>
      n.type === ChallengeNoteType.Text
        ? { id: n.id, type: n.type, contents: n.contents! }
        : { id: n.id, type: n.type, url: n.url! },
    );

  const onClickCloseIcon = () => {
    toggleNotes();
    trackAmplitudeEvent({ name: 'Lab - Challenge - Closed notes' });
  };

  const onClickShare = async () => {
    try {
      if (!invite) throw new Error('No invite was found. Please try again later.');

      const inviteURL = getShareURL(teamSlug, invite.id);

      await navigator.clipboard.writeText(inviteURL);

      trackAmplitudeEvent({
        name: 'Lab - Clicked Share',
        data: {
          from: 'Notes screen',
          invite_access_setting: getAccessSetting({
            allowAnonymous: Boolean(invite.allowAnonymous),
            allowedEmailAddressesOnly: Boolean(invite.allowedEmailAddresses?.length),
          }),
        },
      });
    } catch (error) {
      dispatch('error', 'Failed to copy link', error instanceof Error ? error.message : '', 5);
    } finally {
      dispatch('success', 'Link copied', 'You can now share this collection of content.', 5);
    }
  };

  return (
    <DarkOverlay tw="pb-2">
      <Fragment>
        <div tw="absolute right-[20px] top-[20px] flex items-center">
          {showShareButton && <ShareButton onClick={onClickShare} tw="mr-1 z-overlay" />}
          <Close onClick={onClickCloseIcon} tw="p-1">
            <CloseIcon tw="h-4 text-white" />
          </Close>
        </div>
        <ChallengeNotes
          tw="flex-grow"
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          notes={filteredNotes}
        />
        <CarouselPagination
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          size={filteredNotes.length}
        />
      </Fragment>
    </DarkOverlay>
  );
};
export default NotesOverlay;
